/**
 * @file OrderShipmentsTab.tsx
 * @description Вкладка «Отгрузки» заказа (phase10; после объединения доставки —
 * phase19.24 шаг 5.2 §5).
 *
 * Здесь только физика: отгрузка, трек, события. Условия доставки (способ,
 * адрес, стоимость, согласование) живут во вкладке «Доставка» — сверху
 * остаётся строка-ссылка `ShipmentsDeliveryLink`, чтобы кладовщик видел, чем
 * и куда едет заказ, и мог открыть условия одним нажатием. Сами условия
 * приходят пропом: `GET …/delivery` карточка запрашивает один раз на всех
 * (`OrderDetailPage`) — два ответа про одни условия успевали разойтись.
 *
 * Расчёт перевозчика отсюда запускается только ДЛЯ ОТГРУЗКИ: он проверяет
 * параметры конкретной посылки и сумму заказа не меняет (F26). Расчёт,
 * который меняет цену клиенту, живёт во вкладке «Доставка».
 */

import { Truck } from "lucide-react";
import { useEffect, useState } from "react";

import { CreateShipmentSheet } from "./CreateShipmentSheet";
import { DeliveryQuoteSheet } from "./DeliveryQuoteSheet";
import { OrderShipmentsList } from "./OrderShipmentsList";
import { SdekPickupPointPicker } from "./SdekPickupPointPicker";
import { ShipmentsDeliveryLink } from "./ShipmentsDeliveryLink";
import { useShipmentActions } from "./useShipmentActions";
import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import { ApiError } from "../../lib/api-client";
import type { DeliveryAgreementDto } from "../../lib/api-delivery-agreement";
import { listShipments, type OrderDto, type ShipmentDto } from "../../lib/api-orders";
import type { OrderDeliveryState } from "../../hooks/useOrderDelivery";
import { useOrderTabRevisit } from "../../hooks/useOrderTabRevisit";
import {
  orderDeliveryReceipt,
  shipmentCarrierForReceipt,
} from "../../lib/order-delivery-view";
import { isOrderRefunded } from "../../lib/order-summary-lines";
import { OrderTabSkeleton } from "./OrderTabSkeleton";

interface Props {
  order: OrderDto;
  /** Заявка F30: из неё берём отметку «клиент подтвердил условия». */
  agreement: DeliveryAgreementDto | null;
  /** Общий на карточку ответ `GET …/delivery`: условия, позиции, адрес. */
  delivery: OrderDeliveryState;
  canManage: boolean;
  /** Slug магазина — для ссылок на настройки перевозчика из расчёта. */
  shopSlug?: string;
  onChanged?: () => void;
  /** id отгрузки из `?focus=` — переход из узла «Отгрузка» (phase19.24 §4). */
  focusId?: string | null;
  /** Открыть вкладку «Доставка» — единственный дом условий доставки. */
  onOpenDelivery: () => void;
  /** Вкладка сейчас открыта: при возврате на неё список дочитывается тихо. */
  active?: boolean;
}

export function OrderShipmentsTab({
  order,
  agreement,
  delivery,
  canManage,
  shopSlug,
  onChanged,
  focusId,
  onOpenDelivery,
  active = true,
}: Props) {
  const [items, setItems] = useState<ShipmentDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [pickerOpenFor, setPickerOpenFor] = useState<string | null>(null);
  /** null — панель закрыта; иначе id отгрузки, для которой считаем тариф. */
  const [quoteFor, setQuoteFor] = useState<string | null>(null);

  /** Список отгрузок вкладки: условия доставки держит общий хук карточки. */
  async function loadShipments(): Promise<void> {
    try {
      setItems(await listShipments(order.id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось");
    }
  }

  /** После действия перечитываем и отгрузки, и условия: тариф мог примениться. */
  async function refresh(): Promise<void> {
    await loadShipments();
    await delivery.reload();
  }

  useEffect(() => {
    void loadShipments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id]);
  useOrderTabRevisit(active, loadShipments);

  const actions = useShipmentActions(async () => {
    await refresh();
    onChanged?.();
  });

  if (error) return <Alert severity="error">{error}</Alert>;
  if (items === null) {
    return (
      // Заготовка высотой блока, а не спиннер: страница не прыгает (qa4 п. 14).
      <OrderTabSkeleton />
    );
  }

  const drafts = items.filter((s) => s.status === "PENDING" && !s.trackNumber);
  const quoteShipment = quoteFor ? (items.find((s) => s.id === quoteFor) ?? null) : null;
  const deliveryData = delivery.data;
  const goodsTotal = Number(order.totalGross) - (deliveryData?.deliveryItem?.priceGross ?? 0);
  const receipt = orderDeliveryReceipt(order, deliveryData, agreement);
  // Выбор клиента на витрине — уже согласие: он оформлял заказ с этой ценой.
  // Для условий менеджера согласием считается только подтверждение по ссылке.
  const confirmedAt =
    agreement?.confirmedAt ??
    (receipt?.source === "SITE" ? (receipt.setAt ?? order.createdAt) : null);

  return (
    <div className="flex flex-col gap-3">
      {delivery.error ? (
        <Alert severity="warning" title="Доставка по заказу недоступна">
          {delivery.error}
        </Alert>
      ) : (
        <ShipmentsDeliveryLink
          receipt={receipt}
          confirmedAt={confirmedAt}
          onOpenDelivery={onOpenDelivery}
        />
      )}

      {canManage ? (
        <div className="flex justify-end">
          <Button onClick={() => setCreating(true)} size="sm">
            <Truck className="mr-1 size-4" />
            Новая отгрузка
          </Button>
        </div>
      ) : null}

      <OrderShipmentsList
        items={items}
        focusId={focusId}
        canManage={canManage}
        orderRefunded={isOrderRefunded(order)}
        acting={actions.acting}
        printing={actions.printing}
        delivery={deliveryData}
        onQuote={setQuoteFor}
        onPickPoint={setPickerOpenFor}
        onSendToSdek={actions.sendToSdek}
        onSyncSdek={actions.syncSdek}
        onPrint={actions.print}
        onShip={actions.ship}
        onDeliver={actions.deliver}
        onDelete={actions.remove}
        onSaveTrack={actions.saveTrackNumber}
      />

      {creating ? (
        <CreateShipmentSheet
          orderId={order.id}
          orderNumber={order.number}
          // Получатель, ПВЗ, тариф и места — из согласованных условий:
          // кладовщик видит их сводкой и чек-листом, а не вводит заново.
          delivery={deliveryData}
          receipt={receipt}
          defaultCarrier={shipmentCarrierForReceipt(receipt)}
          pickupOnly={receipt?.method === "PICKUP"}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void refresh();
            onChanged?.();
          }}
        />
      ) : null}

      <DeliveryQuoteSheet
        open={quoteFor !== null}
        onClose={() => setQuoteFor(null)}
        orderId={order.id}
        shopSlug={shopSlug}
        delivery={deliveryData}
        drafts={drafts}
        shipment={quoteShipment}
        goodsTotal={Number.isFinite(goodsTotal) ? goodsTotal : undefined}
        onApplied={() => {
          void refresh();
          onChanged?.();
        }}
      />

      <SdekPickupPointPicker
        open={pickerOpenFor !== null}
        onClose={() => setPickerOpenFor(null)}
        onPick={(p) => {
          if (pickerOpenFor) {
            actions.savePickupPoint(pickerOpenFor, p.externalCode, p.address);
          }
        }}
      />
    </div>
  );
}
